import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from "react";
import { Modal, Button, Input, Typography, Alert, Space } from "antd";

const DialogContext = createContext(null);

export function DialogProvider({ children }) {
  const resolverRef = useRef(null);
  const [dialogState, setDialogState] = useState({
    open: false,
    type: null, // 'alert' | 'confirm'
    title: "",
    message: "",
    okText: "",
    cancelText: "",
    reportBug: null, // optional payload to show a tiny feedback form
  });

  const [reportText, setReportText] = useState("");
  const [reportError, setReportError] = useState("");
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  const closeDialog = useCallback(() => {
    setDialogState((s) => ({ ...s, open: false }));
  }, []);

  const openDialog = useCallback((config) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialogState({
        open: true,
        type: config.type || "alert",
        title: config.title || (config.type === "confirm" ? "Confirm" : "Notice"),
        message: config.message || "",
        okText:
          config.okText ||
          (config.type === "confirm" ? "OK" : "Got it"),
        cancelText:
          config.type === "confirm"
            ? config.cancelText || "Cancel"
            : "",
        reportBug: config.reportBug || null,
      });
    });
  }, []);

  const confirm = useCallback(
    (message, options = {}) =>
      openDialog({
        type: "confirm",
        message,
        ...options,
      }),
    [openDialog]
  );

  const alert = useCallback(
    (message, options = {}) =>
      openDialog({
        type: "alert",
        message,
        ...options,
      }),
    [openDialog]
  );

  useEffect(() => {
    // Reset report state each time we open a new dialog
    if (dialogState.open) {
      setReportError("");
      setIsSendingReport(false);
      setReportSent(false);

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogState.open]);

  const sendReport = useCallback(async () => {
    try {
      setReportError("");
      setIsSendingReport(true);

      const alertName = (dialogState.reportBug?.alertName || dialogState.title || "Alert").toString();
      const fields = Array.isArray(dialogState.reportBug?.fields) ? dialogState.reportBug.fields : [];
      const bodyText = (reportText || "").trim();
      const token = localStorage.getItem("token");

      if (!token) {
        setReportError("You need to be logged in to send this feedback.");
        return;
      }

      const payloadText =
        `[ALERT FEEDBACK]\n` +
        `Alert: ${alertName}\n` +
        (fields.length ? `Requested:\n${fields.map((f) => `- ${f}`).join("\n")}\n` : "") +
        `\nResponse:\n${bodyText || "(empty)"}\n` +
        `\nContext:\n` +
        `- url: ${String(window.location?.href || "")}\n` +
        `- ua: ${String(navigator.userAgent || "")}\n`;

      const fd = new FormData();
      fd.append("feedback", payloadText);

      const doSend = async (accessToken) => {
        const resp = await fetch("/api/send_feedback/", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: fd,
        });
        return resp;
      };

      let resp = await doSend(token);
      if (resp.status === 401) {
        // Try token refresh once
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          const refreshResp = await fetch("/api/refresh_tokens/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          if (refreshResp.ok) {
            const data = await refreshResp.json().catch(() => ({}));
            if (data?.access_token) localStorage.setItem("token", data.access_token);
            if (data?.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
            if (data?.access_token) resp = await doSend(data.access_token);
          }
        }
      }

      if (!resp.ok) {
        const txt = await resp.text().catch(() => "");
        setReportError(txt || "Error sending feedback. Please try again.");
        return;
      }

      setReportSent(true);
    } catch (e) {
      setReportError("Error sending feedback. Please try again.");
    } finally {
      setIsSendingReport(false);
    }
  }, [dialogState.reportBug, dialogState.title, reportText]);

  const handleOk = useCallback(() => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    closeDialog();
    if (dialogState.type === "confirm") {
      resolve && resolve(true);
    } else {
      resolve && resolve();
    }
  }, [closeDialog, dialogState.type]);

  const handleCancel = useCallback(() => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    closeDialog();
    if (dialogState.type === "confirm") {
      resolve && resolve(false);
    } else {
      resolve && resolve();
    }
  }, [closeDialog, dialogState.type]);

  const value = useMemo(
    () => ({ confirm, alert }),
    [confirm, alert]
  );

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Modal
        title={dialogState.title}
        open={dialogState.open}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={dialogState.okText}
        cancelText={dialogState.cancelText || undefined}
        rootClassName="liquid-glass-modal"
        footer={
          dialogState.type === "confirm" ? (
            <>
              <Button onClick={handleCancel}>
                {dialogState.cancelText || "Cancel"}
              </Button>
              <Button type="primary" className="primary-glass-btn" onClick={handleOk}>
                {dialogState.okText || "OK"}
              </Button>
            </>
          ) : (
            <Space>
              <Button onClick={handleOk}>{dialogState.okText || "Close"}</Button>
              {dialogState.reportBug ? (
                <Button
                  type="primary"
                  className="primary-glass-btn"
                  onClick={sendReport}
                  disabled={reportSent}
                  loading={isSendingReport}
                >
                  {reportSent ? "Sent" : (dialogState.reportBug?.sendText || "Send")}
                </Button>
              ) : null}
            </Space>
          )
        }
      >
        <div style={{ whiteSpace: "pre-wrap" }}>{dialogState.message}</div>
        {dialogState.type === "alert" && dialogState.reportBug ? (
          <div style={{ marginTop: 12 }}>
            <Typography.Text strong>To help me improve, please share:</Typography.Text>
            {Array.isArray(dialogState.reportBug?.fields) && dialogState.reportBug.fields.length ? (
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 22 }}>
                {dialogState.reportBug.fields.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            ) : null}
            <Input.TextArea
              rows={4}
              placeholder={dialogState.reportBug?.placeholder || "Type the requested info here..."}
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              disabled={reportSent}
            />
            {reportSent ? (
              <Alert style={{ marginTop: 10 }} type="success" showIcon message="Thanks - sent!" />
            ) : null}
            {reportError ? (
              <Alert style={{ marginTop: 10 }} type="error" showIcon message={reportError} />
            ) : null}
          </div>
        ) : null}
      </Modal>
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return ctx;
}

